// ─────────────────────────────────────────────────────────────────────
// Task Board sub-sections (Aug 20)
//
// A full board is a board you scroll past things on. Like goes with like:
// the flat list folds into labelled accordion groups, each header carrying
// its own overdue / urgent count so a CLOSED group can never hide
// something that needs you today.
//
// Pure logic lives here (no JSX) so it can be unit-tested — see
// __tests__/taskGroups.test.js.
// ─────────────────────────────────────────────────────────────────────

const PRIORITY_COLORS = { urgent: '#dc2626', high: '#d97706', normal: '#3b82f6', low: '#6b7280' };
const PRIORITY_LABELS = { urgent: 'URGENT', high: 'HIGH', normal: 'NORMAL', low: 'LOW' };
const SOURCE_LABELS = { expense: 'Expense', refund_request: 'Refund', new_account: 'New Account' };
const SOURCE_COLORS = { expense: '#f59e0b', refund_request: '#8b5cf6', new_account: '#3b82f6' };

const GROUP_BY_OPTIONS = [
  { key: 'type', label: 'Group: Type' },
  { key: 'stage', label: 'Group: Stage' },
  { key: 'priority', label: 'Group: Priority' },
  { key: 'none', label: 'No grouping' },
];
const PRIORITY_ORDER = ['urgent', 'high', 'normal', 'low'];
// Deterministic colour per label, so a workflow keeps the same stripe on
// every visit (and for every user) without storing anything.
const GROUP_PALETTE = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ec4899', '#06b6d4', '#ef4444', '#6366f1'];
function colorFor(label) {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return GROUP_PALETTE[h % GROUP_PALETTE.length];
}

function isOverdueItem(i) {
  return Boolean(i.sla_deadline) && new Date(i.sla_deadline) < new Date() && !i.completed_at;
}

// Type = the workflow the submission came from ("Sign Request", "Refund",
// "Expense"…). That's what "of a likeness" means on this board.
function groupOf(item, groupBy) {
  if (groupBy === 'stage') {
    return { key: item.stage_name || 'No stage', label: item.stage_name || 'No stage', color: item.stage_color || '#6b7280' };
  }
  if (groupBy === 'priority') {
    const p = item.priority || 'normal';
    return { key: p, label: PRIORITY_LABELS[p] || p.toUpperCase(), color: PRIORITY_COLORS[p] || '#6b7280' };
  }
  const label = item.workflow_name || SOURCE_LABELS[item.source_type] || 'Other';
  return { key: label, label, color: SOURCE_COLORS[item.source_type] || colorFor(label) };
}

function buildGroups(items, groupBy) {
  const map = new Map();
  for (const item of items) {
    const g = groupOf(item, groupBy);
    if (!map.has(g.key)) map.set(g.key, { ...g, items: [], overdue: 0, urgent: 0 });
    const bucket = map.get(g.key);
    bucket.items.push(item);
    if (isOverdueItem(item)) bucket.overdue++;
    if (item.priority === 'urgent' && !item.completed_at) bucket.urgent++;
  }
  const groups = [...map.values()];
  // Priority groups keep their natural severity order; everything else
  // floats the groups that need attention to the top, then goes by size.
  if (groupBy === 'priority') {
    groups.sort((a, b) => PRIORITY_ORDER.indexOf(a.key) - PRIORITY_ORDER.indexOf(b.key));
  } else {
    groups.sort((a, b) =>
      (b.overdue > 0) - (a.overdue > 0) ||
      (b.urgent > 0) - (a.urgent > 0) ||
      b.items.length - a.items.length ||
      a.label.localeCompare(b.label));
  }
  return groups;
}

// Collapsed state lives in the browser: fold the buckets you don't work
// out of once, and they stay folded next time you open the board.
const COLLAPSE_KEY = 'taskboard.collapsed.v1';
const GROUPBY_KEY = 'taskboard.groupBy.v1';
function readStore(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}
function writeStore(key, value) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
}


export {
  PRIORITY_COLORS, PRIORITY_LABELS, SOURCE_LABELS, SOURCE_COLORS,
  GROUP_BY_OPTIONS, PRIORITY_ORDER,
  colorFor, isOverdueItem, groupOf, buildGroups,
  COLLAPSE_KEY, GROUPBY_KEY, readStore, writeStore,
};
